package fr.frux.splash

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.semantics.clearAndSetSemantics
import fr.frux.core.designsystem.a11y.rememberReduceMotion
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.conflate

/**
 * Fond crème du splash, identique dans les deux thèmes : la palette du logo est fixe (encre, mousse,
 * argile) et n'est lisible que sur ce fond. Partagé avec le visuel de marque des écrans d'entrée.
 */
internal val FOND_SPLASH = Color(0xFFFAF8F2)

/**
 * Durée (s) restant à jouer pour atteindre [SplashTimeline.TOTAL] depuis une progression déjà
 * acquise (ex. restaurée après rotation). Jamais négative. Pure, testable indépendamment de Compose.
 */
internal fun splashRemainingDuration(currentProgress: Float): Float {
    val remaining = SplashTimeline.TOTAL - currentProgress
    return remaining.coerceAtLeast(0f)
}

/** Splash de cold start : anime le logo une fois puis s'efface. Skippable au tap ; statique si reduce motion. */
@Composable
fun SplashOverlay(
    onFinished: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val reduceMotion = rememberReduceMotion()
    var skipped by remember { mutableStateOf(false) }
    // Ratio de progression sauvé (pas juste en mémoire composition) : une rotation recrée l'activity
    // et rejoue toute la composition, mais SavedStateRegistry survit → l'animation reprend où elle
    // en était au lieu de repartir de zéro (#200).
    var savedProgress by rememberSaveable { mutableStateOf(0f) }
    val progress = remember { Animatable(if (reduceMotion) SplashTimeline.TOTAL else savedProgress) }
    val overlayAlpha = remember { Animatable(1f) }
    // Le fondu ne doit plus consommer de taps : sans ça, un tap pendant les 250 ms de fondu-out
    // reste piégé par le pointerInput (jamais transmis à l'app dessous) sans effet utile.
    var fading by remember { mutableStateOf(false) }

    LaunchedEffect(progress) {
        // Conflation + délai : la progression n'est sauvée que ~10 fois/s — elle ne sert qu'à la
        // reprise après rotation, une écriture d'état saveable par frame serait du travail gratuit.
        snapshotFlow { progress.value }.conflate().collect {
            savedProgress = it
            delay(100)
        }
    }

    LaunchedEffect(skipped) {
        if (skipped) {
            progress.snapTo(SplashTimeline.TOTAL)
        } else if (!reduceMotion) {
            val remaining = splashRemainingDuration(progress.value)
            if (remaining > 0f) {
                // Durée proportionnelle au restant : le rythme (linéaire) ne change pas, l'animation
                // reprend à vitesse constante au lieu de sauter ou de rejouer depuis le début.
                progress.animateTo(
                    SplashTimeline.TOTAL,
                    tween((remaining * 1000).toInt(), easing = LinearEasing),
                )
            }
        } else {
            delay(600) // frame finale statique, brève, puis on s'efface
        }
        fading = true
        overlayAlpha.animateTo(0f, tween(250))
        onFinished()
    }

    Box(
        modifier
            .fillMaxSize()
            .graphicsLayer { alpha = overlayAlpha.value }
            .background(FOND_SPLASH)
            .then(if (fading) Modifier else Modifier.pointerInput(Unit) { detectTapGestures { skipped = true } })
            // Le contenu du splash est purement décoratif (logo, wordmark, tagline) : rien à annoncer
            // à TalkBack/VoiceOver, et surtout pas les deux Text sous-jacents.
            .clearAndSetSemantics {},
    ) {
        FruxSplashLogo(t = { progress.value })
    }
}
